import { Button, FormGroup, InputGroup, Intent, Spinner, SpinnerSize, Icon } from '@blueprintjs/core'
import { lang } from 'botpress/shared'
import React, { FC, useState, useRef, useEffect } from 'react' // added useEffect
import { useHistory } from 'react-router-dom'
import '../app/Wizard/style.css'
import packageJson from '../../../../package.json'

interface Props { }

export const ChangePasswordForm: FC<Props> = props => {
  const [email, setEmail] = useState('')
  const [isEmailValid, setEmailValid] = useState(false)
  const [emailMsg, setEmailMsg] = useState('')
  const [emailChecked, setEmailChecked] = useState(false)

  const [otp, setOtp] = useState(['', '', '', '']) // Array for 4 OTP boxes
  const [isOtpValid, setOtpValid] = useState(false)
  const [otpChecked, setOtpChecked] = useState(false)
  const [otpSentMessage, setOtpSentMessage] = useState('')
  const [resendCountdown, setResendCountdown] = useState(0) // New state for timer
  const [otpErrorMsg, setOtpErrorMsg] = useState('')

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passSuccess, setPassSuccess] = useState(false)
  const [passChecked, setPassChecked] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)

  const [isLoading, setIsLoading] = useState(false)
  const history = useHistory()

  const otpRefs = useRef<HTMLInputElement[]>([]) // Refs for OTP input fields

  const CURRENT_VERSION = packageJson.version
  const API_URL = process.env.REACT_APP_API_URL || 'https://www.app.xmati.ai/apis'

  const validatePassword = (password: string) => {
    const minLength = 8
    const maxLength = 16
    const hasUpperCase = /[A-Z]/.test(password)
    const hasLowerCase = /[a-z]/.test(password)
    const hasNumber = /[0-9]/.test(password)
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password)

    if (password.length < minLength) {
      return `Password must be at least ${minLength} characters long`
    }
    if (password.length > maxLength) {
      return `Password must not be more than ${maxLength} characters`
    }
    if (!hasUpperCase) {
      return 'Password must contain at least one uppercase letter'
    }
    if (!hasLowerCase) {
      return 'Password must contain at least one lowercase letter'
    }
    if (!hasNumber) {
      return 'Password must contain at least one number'
    }
    if (!hasSpecialChar) {
      return 'Password must contain at least one special character'
    }
    return null
  }

  const handlePasswordChange = (password: string) => {
    setNewPassword(password)
    const error = validatePassword(password)
    setPasswordError(error)
  }

  const onSubmit = async e => {
    e.preventDefault()
    try {
      if (!isEmailValid) {
        await checkEmail(false)
      } else {
        await updatePass()
      }
    } catch (error) {
      // Display error message for any step
      setEmailMsg(error.message || 'An unexpected error occurred. Please try again.')
      setIsLoading(false)
    }
  }

  const checkEmail = async (resend: boolean) => {
    setIsLoading(true)
    setEmailChecked(false)
    setOtpChecked(false)
    setOtpErrorMsg('')
    if (resend) {
      setOtp(['', '', '', ''])
    }
    try {
      const result = await fetch(`${API_URL}/check-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-App-Version': CURRENT_VERSION
        },
        body: JSON.stringify({
          email: email.trim(),
          from: 'forgot-pass',
        }),
      })

      const data = await result.json()
      setEmailChecked(true)
      if (result.status === 500) {
        setEmailValid(false)
        setEmailMsg(data.message || 'Internal server error')
      } else if (result.status === 429) {
        setEmailValid(false)
        setEmailMsg(data.message || 'Too many attempts. Please try again after 1 hour.')
      } else if (result.status === 200) {
        setEmailValid(true)
        setEmailMsg('')

        const message = data.message || 'If this email address is associated with an account, an otp has been sent to this email'
        setOtpSentMessage(message)
        setResendCountdown(30) // Start the 30 sec timer
      } else if (result.status === 400) {
        setEmailValid(false)
        setEmailMsg(data.message || 'Email not registered')
      }
    } catch (error) {
      setEmailMsg('Error checking email.. please try again later')
      setEmailValid(false)
    } finally {
      setIsLoading(false)
    }
  }

  // const validateOtp = async () => {
  //   setIsLoading(true)
  //   setOtpChecked(false)
  //   try {
  //     const enteredOtp = otp.join('') // Combine the 4 OTP boxes into a single string
  //     if (enteredOtp === receivedOtp.toString()) {
  //       setOtpValid(true)
  //       setOtpChecked(true)
  //     } else {
  //       setOtpValid(false)
  //       setOtpChecked(true)
  //     }
  //   } catch (error) {
  //     setOtpValid(false)
  //   } finally {
  //     setIsLoading(false)
  //   }
  // }

  const updatePass = async () => {
    setIsLoading(true)
    setPassChecked(false)
    try {
      const enteredOtp = otp.join('')

      const result = await fetch(`${API_URL}/forgot-pass`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-App-Version': CURRENT_VERSION
        },
        body: JSON.stringify({
          email: email.trim(),
          otp: enteredOtp,
          password: newPassword.trim(),
        }),
      })

      const data = await result.json()
      if (result.status === 200) {
        setOtpValid(true)
        setOtpChecked(true)
        setPassSuccess(true)
        setPassChecked(true)
        history.push({
          pathname: '/login',
        })
      } else {
        setPassSuccess(false)
        setPassChecked(true)
        setOtpValid(false)
        setOtpErrorMsg(data.msg || 'Invalid OTP')
        setOtpChecked(true)
      }
    } catch (error) {
      setPassSuccess(false)
      setOtpValid(false)
      setOtpErrorMsg('Something went wrong. Please try again.')
      setOtpChecked(true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1) {
      const newOtp = [...otp]
      newOtp[index] = value
      setOtp(newOtp)
      setOtpChecked(false)
      setOtpErrorMsg('')

      // Automatically focus the next input field
      if (value && index < otpRefs.current.length - 1) {
        otpRefs.current[index + 1]?.focus()
      }

      // If clearing, focus back to the current or previous input field
      if (!value && index > 0) {
        otpRefs.current[index - 1]?.focus()
      }
    }
  }

  // useEffect to handle countdown
  useEffect(() => {
    if (resendCountdown > 0) {
      const timerId = setTimeout(() => {
        setResendCountdown(resendCountdown - 1)
      }, 1000)
      return () => clearTimeout(timerId)
    }
  }, [resendCountdown])

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <form onSubmit={onSubmit} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div className='input-container' style={{ height: '40px', padding: '0 10px', marginBottom: '12px' }}>
          <Icon icon="envelope" className="input-icon" style={{ marginRight: '10px' }} />
          <input
            className='custom-input'
            tabIndex={1}
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            name="email"
            id="email"
            disabled={isLoading || isEmailValid}
            autoFocus={true}
          />
        </div>
        {emailChecked && !isEmailValid && (
          <div className="error" style={{ marginBottom: '10px', fontSize: '12px' }}>
            {emailMsg}
          </div>
        )}

        {!isEmailValid && (
          <div className='button-container' style={{ width: '80%' }}>
            <button
              tabIndex={2}
              type="submit"
              id="btn-check-email"
              disabled={isLoading || !email}
              style={{
                width: '100%',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                padding: '8px 16px'
              }}
            >
              {isLoading && <div className="loader" style={{ width: '16px', height: '16px', margin: '0' }}></div>}
              Check Email
            </button>
          </div>
        )}

        {isEmailValid && !isOtpValid && (
          <>
            <div className='stepSubtitle' style={{ color: 'green', marginBottom: '10px', textAlign: 'center', fontSize: '13px' }}>{otpSentMessage}</div>
            <div className='stepSubtitle' style={{ marginBottom: '8px', fontSize: '13px', textAlign: 'center', width: '100%' }}>Enter the 4-digit OTP</div>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', justifyContent: 'center' }}>
              {otp.map((value, index) => (
                <input
                  key={index}
                  className='custom-input'
                  value={value}
                  onChange={e => handleOtpChange(index, e.target.value)}
                  type="text"
                  maxLength={1}
                  style={{
                    width: '42px',
                    height: '42px',
                    textAlign: 'center',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    borderRadius: '8px',
                    border: '1px solid #f2f2f2',
                    backgroundColor: '#f9f9f9',
                    padding: 0
                  }}
                  disabled={isLoading}
                  ref={el => (otpRefs.current[index] = el!)}
                />
              ))}
            </div>
            <div style={{ fontSize: '12px', color: '#777', textAlign: 'center', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
              <Icon icon="info-sign" iconSize={12} color="#777" />
              <span>This OTP will expire in 10 minutes.</span>
            </div>
            {otpChecked && !isOtpValid && (
              <div className="error" style={{ marginBottom: '10px', fontSize: '12px', textAlign: 'center', width: '100%' }}>
                {otpErrorMsg}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', fontSize: '12px', marginBottom: '15px' }}>
              <span style={{ color: '#666' }}>Didn't receive the OTP?</span>
              {resendCountdown > 0 ? (
                <span style={{ color: '#999', fontWeight: 'bold' }}>
                  Resend in {resendCountdown}s
                </span>
              ) : (
                <span
                  style={{ color: '#05a8e1', cursor: 'pointer', fontWeight: 'bold', textDecoration: 'underline' }}
                  onClick={() => !isLoading && checkEmail(true)}
                >
                  Resend OTP
                </span>
              )}
            </div>

            <div className='stepSubtitle' style={{ marginBottom: '8px', marginTop: '5px', fontSize: '13px', width: '80%', textAlign: 'left' }}>Create New Password</div>
            <div className='input-container' style={{ height: '40px', padding: '0 10px', marginBottom: '10px' }}>
              <Icon icon="lock" className="input-icon" style={{ marginRight: '10px' }} />
              <input
                className='custom-input'
                tabIndex={6}
                type={showNewPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                value={newPassword}
                onChange={e => handlePasswordChange(e.target.value)}
                name="newPassword"
                id="newPassword"
                disabled={isLoading}
              />
              <Icon
                icon={showNewPassword ? 'eye-off' : 'eye-open'}
                className="eye-icon"
                onClick={() => setShowNewPassword(!showNewPassword)}
                style={{ cursor: 'pointer' }}
              />
            </div>
            {passwordError && (
              <div className="error" style={{ marginBottom: '8px', fontSize: '12px' }}>
                {passwordError}
              </div>
            )}
            <div className='input-container' style={{ height: '40px', padding: '0 10px', marginBottom: '10px' }}>
              <Icon icon="lock" className="input-icon" style={{ marginRight: '10px' }} />
              <input
                className='custom-input'
                tabIndex={7}
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                name="confirmPassword"
                id="confirmPassword"
                disabled={isLoading}
              />
              <Icon
                icon={showConfirmPassword ? 'eye-off' : 'eye-open'}
                className="eye-icon"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{ cursor: 'pointer' }}
              />
            </div>

            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <div className="error" style={{ marginBottom: '8px', fontSize: '12px' }}>
                Passwords don't match
              </div>
            )}

            {!passSuccess && passChecked && (
              <div className="error" style={{ marginBottom: '8px', fontSize: '12px' }}>
                Password failed to update
              </div>
            )}

            <div className='button-container' style={{ width: '80%', marginBottom: '5px' }}>
              <button
                tabIndex={8}
                type="submit"
                id="btn-change-pass"
                disabled={
                  isLoading ||
                  otp.some(value => value === '') ||
                  !newPassword ||
                  !confirmPassword ||
                  newPassword !== confirmPassword ||
                  !!passwordError
                }
                style={{
                  width: '100%',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  padding: '8px 16px'
                }}
              >
                {isLoading && <div className="loader" style={{ width: '16px', height: '16px', margin: '0' }}></div>}
                Update Password
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  )
}
